import { Stack } from "@mantine/core"

import {
  updateOperationsValidationType,
  updateOperationValidationType,
} from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.request.dto"

import { queryClient } from "~/backend/queryClient"
import { findOperationTreeByProductIdsQueryKey } from "~/gen/api"
import { useBatchUpdateModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext"
import { useTraceabilityOperations } from "~/pages/SupplierPortal/TraceabilityRequests/context/useTraceabilityOperations"
import { useUpdateOperationMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks"
import { OperationItem } from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"

import {
  BatchPopoverOperationItems,
  BatchPopoverOperationItem,
  BatchPopoverOperationItemType,
} from "./BatchPopoverOperationItem"
import { OperationCustomFields } from "./OperationCustomFields"

export const OperationFields = () => {
  const { setBatchModalOpen } = useBatchUpdateModalContext()
  const { user } = useInformationRequestContext()
  const {
    operationItem,
    request,
    isTransferred,
    currentContactId,
    updateOperationAndChildren,
  } = useTraceabilityOperations()
  const { mutate: updateOperation, isPending: isUpdating } =
    useUpdateOperationMutation(operationItem.operation?.id, {
      onSuccess: async () => {
        setBatchModalOpen(false)
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["listTraceabilityRequests", request.original.id],
          }),
          queryClient.invalidateQueries({
            queryKey: findOperationTreeByProductIdsQueryKey({
              productIds: [],
            }),
          }),
        ])
      },
    })

  const canShowContactSelect = Boolean(user && operationItem.operation)

  const STANDARD_OPERATION_ITEMS = getStandardOperationItems(
    isTransferred,
    currentContactId,
    operationItem,
    updateOperation,
    updateOperationAndChildren,
    canShowContactSelect
  )

  return (
    <Stack gap="xs">
      {STANDARD_OPERATION_ITEMS.map((item, index) => (
        <BatchPopoverOperationItem<typeof item.value>
          key={`${item.type}-${index}`}
          type={item.type}
          value={item.value}
          onChange={item.onChange as (value: typeof item.value) => void}
        />
      ))}
      <OperationCustomFields isLoading={isUpdating} />
    </Stack>
  )
}

const getStandardOperationItems = (
  isTransferred: boolean,
  currentContactId: string,
  operationItem: OperationItem,
  updateOperation: (params: updateOperationValidationType) => void,
  updateOperationAndChildren: (
    update: updateOperationsValidationType["update"]
  ) => void,
  canShowContactSelect: boolean
) => {
  const items: Array<{
    type: BatchPopoverOperationItemType
    value: unknown
    onChange:
      | ((contactId: string) => void)
      | ((purchaseOrderRef: string | null) => void)
      | ((reference: string | null) => void)
      | ((productionSiteId: string | null) => void)
  }> = []

  if (canShowContactSelect) {
    items.push({
      type: BatchPopoverOperationItems.contact,
      value: operationItem.operation?.contact.id,
      onChange: (contactId: string) => {
        updateOperation({
          contactId,
          doNotKnow: contactId !== currentContactId,
          productionSiteId: null,
        })
        if (contactId !== currentContactId) {
          updateOperationAndChildren({
            contactId,
            doNotKnow: true,
            productionSiteId: null,
          })
        }
      },
    })
  }

  items.push(
    {
      type: BatchPopoverOperationItems.purchaseOrderRef,
      value: operationItem.operation?.purchaseOrderRef,
      onChange: (purchaseOrderRef: string | null) => {
        updateOperation({ purchaseOrderRef })
      },
    },
    {
      type: BatchPopoverOperationItems.reference,
      value: operationItem.operation?.reference,
      onChange: (reference: string | null) => {
        updateOperation({ reference })
      },
    }
  )

  if (!isTransferred) {
    items.push({
      type: BatchPopoverOperationItems.productionSite,
      value: operationItem.operation?.productionSiteId,
      onChange: (productionSiteId: string | null) => {
        updateOperation({ productionSiteId })
      },
    })
  }

  return items
}
